import { useState } from 'react';
import * as z from 'zod';
import ms from 'ms';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { updateOrganization } from '@/api/organization.api.private';
import { useOrganizationContext } from '../organization.context';
import DurationField, { DAY_MS, HOUR_MS, durationMs } from './duration-field';

const DEFAULTS = {
  enabled: false,
  suggestBiometricsEnrollmentAfterLogin: false,
  expiryMs: 30 * DAY_MS,
  refreshTokenExpiryMs: 12 * HOUR_MS,
};

const SCHEMA = z.object({
  enabled: z.boolean(),
  suggestBiometricsEnrollmentAfterLogin: z.boolean(),
  expiryMs: durationMs({ min: 1, max: 365 * DAY_MS, minLabel: '1 minute' }),
  refreshTokenExpiryMs: durationMs({ min: 1, max: 30 * DAY_MS, minLabel: '1 minute' }),
});

const buildDefaultValues = organization => {
  const bio = organization.internalConfigurations?.biometrics ?? {};
  return {
    enabled: bio.enabled ?? DEFAULTS.enabled,
    suggestBiometricsEnrollmentAfterLogin:
      bio.suggestBiometricsEnrollmentAfterLogin ?? DEFAULTS.suggestBiometricsEnrollmentAfterLogin,
    expiryMs: ms(bio.expiryMs ?? DEFAULTS.expiryMs),
    refreshTokenExpiryMs: ms(bio.refreshTokenExpiryMs ?? DEFAULTS.refreshTokenExpiryMs),
  };
};

export default function BiometricsSettings() {
  const { organization, setOrganization } = useOrganizationContext();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(SCHEMA),
    defaultValues: buildDefaultValues(organization),
  });

  const enabled = form.watch('enabled');

  const onSubmit = async values => {
    setLoading(true);
    const payload = {
      internalConfigurations: {
        biometrics: {
          enabled: values.enabled,
          suggestBiometricsEnrollmentAfterLogin: values.suggestBiometricsEnrollmentAfterLogin,
          expiryMs: ms(values.expiryMs),
          refreshTokenExpiryMs: ms(values.refreshTokenExpiryMs),
        },
      },
    };

    const { data, error } = await updateOrganization({ id: organization.id, data: payload });
    setLoading(false);

    if (error) {
      toast({
        status: 'error',
        title: 'Failed to update biometrics settings',
        description: data?.message ?? 'An error occurred while saving the settings',
        variant: 'destructive',
      });
      return;
    }

    setOrganization(prev => ({ ...prev, ...data }));
    form.reset(buildDefaultValues(data));
    toast({
      status: 'success',
      title: 'Biometrics settings updated',
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Biometric login</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel className="font-bold">Allow biometric login</FormLabel>
                    <FormDescription>
                      Let users in this organization sign in with Face ID, Touch ID, or fingerprint
                      instead of entering a code each time.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="suggestBiometricsEnrollmentAfterLogin"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel className="font-bold">Offer setup after login</FormLabel>
                    <FormDescription>
                      After a successful login, prompt the user to set up biometric login for next
                      time.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!enabled}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DurationField
              name="expiryMs"
              label="Time between full logins"
              description="How long users can keep signing in with biometrics before they must do a full OTP login again. (range: 1 minute – 365 days)"
              placeholder="30d"
              disabled={!enabled}
            />
            <DurationField
              name="refreshTokenExpiryMs"
              label="Time between biometric prompts"
              description="How often users are asked to confirm with Face ID, Touch ID, or fingerprint to keep using the app. (range: 1 minute – 30 days)"
              placeholder="12h"
              disabled={!enabled}
            />

            <Button type="submit" loading={loading} disabled={loading}>
              Save
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
